import { create } from 'zustand';

import { closeOtherDrawers, registerDrawerCloser } from './_drawer-mutex';

import { useInspector } from '@/components/ui/inspector/hooks/useInspector';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import type { AssetSource } from '@/lib/media-studio/route-for-source';
import { registerWorkspaceReset } from '@/lib/workspace/workspaceResetRegistry';

export interface ImageDialogPayload {
  assetId: string;
  imageUrl: string;
  prompt?: string;
  model?: string;
  aspectRatio?: string;
  seed?: string | number;
  createdAt?: string | Date;
  title?: string;
  width?: number;
  height?: number;
  source?: AssetSource;
  thumbnailUrl?: string;
  breadcrumbs?: InspectorBreadcrumbItem[];
  /**
   * Phase 4 — editable text layers.
   * 'text': dialog opens directly into text-overlay mode (lazy-fetches
   * persisted overlay_layers so the Konva editor is pre-populated).
   * 'view' / undefined: default behavior, unchanged.
   */
  openMode?: 'view' | 'text';
}

interface ImageDialogState {
  isOpen: boolean;
  payload: ImageDialogPayload | null;
  /**
   * Phase 4 — imperative signal for the already-open MediaStudioDialog to
   * enter text-overlay mode. Incremented (not boolean) so repeated calls
   * each fire a distinct effect. Read + reset inside DialogBody's useEffect.
   * Use `requestTextMode()` to fire; `clearTextModeRequest()` to reset.
   */
  textModeRequest: number;
  /**
   * Stack of past `imageUrl` values for the current dialog session. Each
   * `setImageUrl` call pushes the previous URL here so `revertToPrevious`
   * can pop it back. Cleared on `close()`. Capped at 10 entries.
   */
  history: string[];
  /**
   * Stack of future `imageUrl` values created by `revertToPrevious`. Each
   * undo pushes the discarded URL here so `redo` can re-apply it. Any new
   * `setImageUrl` (a fresh edit after an undo) clears this stack — there
   * is no longer a coherent "future" once the user branches.
   */
  future: string[];
  /**
   * Pass 8 P0 — Inspector iteration UX. When set to true, ImageBody renders
   * a shimmer overlay on top of the current image so the user sees the
   * iteration is in flight WITHOUT closing the Inspector. The Realtime
   * subscription on `media` inserts then swaps `imageUrl` to the new row
   * and clears `pendingIteration` automatically. `pendingSince` is the ms
   * timestamp the iteration kicked off — the subscription only swaps to
   * media rows whose created_at >= pendingSince to avoid stale matches.
   */
  pendingIteration: boolean;
  pendingSince: number | null;
  open: (payload: ImageDialogPayload) => void;
  close: () => void;
  /**
   * Updates `imageUrl` on the current payload AND pushes the prior URL onto
   * the history stack so the user can undo. Used by every in-place edit
   * (chip-fired, custom-prompt, regenerate, crop save, FAL Flux Kontext).
   *
   * Pass `{ skipHistory: true }` for transient/optimistic swaps (e.g. local
   * blob URL during crop upload) — the blob URL gets revoked after upload
   * succeeds, so we must NOT keep it in history or undo lands on a broken
   * image. The optimistic-then-persisted pattern looks like:
   *   setImageUrl(blobUrl, { skipHistory: true })  // instant preview
   *   setImageUrl(publicUrl)                       // pushes original to history
   */
  setImageUrl: (url: string, options?: { skipHistory?: boolean; historyUrl?: string }) => void;
  /**
   * Pops the most recent prior URL off the stack and applies it. Returns
   * the URL that was reverted FROM (i.e. the URL being discarded), or null
   * if the stack was empty. Toast-based "Undo" affordance calls this. The
   * discarded URL is pushed onto `future` so `redo` can re-apply it.
   */
  revertToPrevious: () => string | null;
  /**
   * Re-applies the most recently undone URL. Returns the URL that was
   * redone TO, or null if there's nothing to redo (no prior undo, or a
   * fresh edit cleared the future stack).
   */
  redo: () => string | null;
  /** Mark an iteration as in-flight. Sets pendingSince = now. */
  setPendingIteration: (pending: boolean) => void;
  /**
   * Phase 4 — signal the currently-open MediaStudioDialog to enter
   * text-overlay mode. Safe to call even when no dialog is open (the
   * MediaStudioDialog effect resets the counter on close). This is the
   * escape hatch for ImageBody's "Add headline" / "Add logo" buttons
   * which cannot prop-drill into the already-rendered DialogBody.
   */
  requestTextMode: () => void;
  /** Reset the textModeRequest counter after DialogBody has consumed it. */
  clearTextModeRequest: () => void;
}

const MAX_HISTORY = 10;

/**
 * Image dialog payload carrier. The dialog rendering itself is owned by
 * the Inspector primitive (`ImageBody`) — calling `open()` here also pushes
 * an image subject onto the Inspector store so the global mount renders.
 *
 * Closing via `close()` clears both stores. The Inspector's own close
 * gestures (Esc, X, overlay click) flow through `ImageBody.onClose` →
 * Inspector.close, which is bridged back to clear this store via the
 * GlobalInspectorMount onOpenChange handler.
 */
export const useImageDialogStore = create<ImageDialogState>((set, get) => ({
  isOpen: false,
  payload: null,
  textModeRequest: 0,
  history: [],
  future: [],
  pendingIteration: false,
  pendingSince: null,
  open: (payload) => {
    // RC-8 (revised): image MUST open as the unified centered MediaStudioDialog —
    // matches /media page behavior. The original visual bug was that the
    // script artifact canvas (right rail) was still visible behind the
    // centered image dialog → looked like overlapping panels. Fix: close
    // every OTHER drawer (script, document, strategy, brand, video) before
    // opening the image, so the centered dialog has clean focus and
    // nothing peeks through behind it.
    closeOtherDrawers('image');
    set({
      isOpen: true,
      payload,
      history: [],
      future: [],
      pendingIteration: false,
      pendingSince: null,
    });
    const inspectorOptions = payload.breadcrumbs
      ? { presentation: 'workspace' as const, breadcrumbs: payload.breadcrumbs }
      : { presentation: 'workspace' as const };
    useInspector
      .getState()
      .openSubject({ kind: 'image', mediaId: payload.assetId }, inspectorOptions);
  },
  close: () => {
    set({
      isOpen: false,
      textModeRequest: 0,
      history: [],
      future: [],
      pendingIteration: false,
      pendingSince: null,
    });
    const inspector = useInspector.getState();
    const top = inspector.stack[inspector.stack.length - 1];
    if (top?.kind === 'image') inspector.close();
  },
  setImageUrl: (url, options) =>
    set((state) => {
      if (!state.payload) return {};
      if (state.payload.imageUrl === url) return { pendingIteration: false, pendingSince: null };
      // skipHistory: transient swap (e.g. optimistic blob URL during upload).
      // Don't pollute history — the blob URL is about to be revoked.
      if (options?.skipHistory) {
        return {
          payload: { ...state.payload, imageUrl: url },
          pendingIteration: false,
          pendingSince: null,
        };
      }
      // historyUrl: allows the caller to specify which URL to push to history
      // instead of the current imageUrl. Used by handleCropSave to push the
      // original URL (not the transient blob URL from the optimistic swap).
      const urlToPush = options?.historyUrl ?? state.payload.imageUrl;
      const nextHistory = [...state.history, urlToPush].slice(-MAX_HISTORY);
      // Fresh edit branches the timeline — there is no longer a coherent
      // "future" once the user makes a new change after undoing.
      return {
        payload: { ...state.payload, imageUrl: url },
        history: nextHistory,
        future: [],
        pendingIteration: false,
        pendingSince: null,
      };
    }),
  revertToPrevious: () => {
    const { payload, history, future } = get();
    if (!payload || history.length === 0) return null;
    const previous = history[history.length - 1];
    const reverted = payload.imageUrl;
    set({
      payload: { ...payload, imageUrl: previous },
      history: history.slice(0, -1),
      // Push the discarded URL onto future so the user can redo it.
      future: [...future, reverted].slice(-MAX_HISTORY),
    });
    return reverted;
  },
  redo: () => {
    const { payload, history, future } = get();
    if (!payload || future.length === 0) return null;
    const next = future[future.length - 1];
    set({
      payload: { ...payload, imageUrl: next },
      // Re-push the current URL onto history (it's now "the past" again).
      history: [...history, payload.imageUrl].slice(-MAX_HISTORY),
      future: future.slice(0, -1),
    });
    return next;
  },
  setPendingIteration: (pending) =>
    set({
      pendingIteration: pending,
      pendingSince: pending ? Date.now() : null,
    }),
  requestTextMode: () => set((s) => ({ textModeRequest: s.textModeRequest + 1 })),
  clearTextModeRequest: () => set({ textModeRequest: 0 }),
}));

registerDrawerCloser('image', () => useImageDialogStore.getState().close());

registerWorkspaceReset('image-dialog-store', () => {
  useImageDialogStore.setState({
    isOpen: false,
    payload: null,
    textModeRequest: 0,
    history: [],
    future: [],
    pendingIteration: false,
    pendingSince: null,
  });
  const inspector = useInspector.getState();
  const top = inspector.stack[inspector.stack.length - 1];
  if (top?.kind === 'image') inspector.close();
});

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as unknown as { __imageDialogStore: typeof useImageDialogStore }).__imageDialogStore =
    useImageDialogStore;
}
